import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { landing as landingApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import AnalyticsTracker from '@/components/AnalyticsTracker';
import { ArrowRight, Brain, Database, Radio, HardDrive, Search, GitBranch, Gauge, Box, Cpu, FileText, Workflow } from 'lucide-react';

const catMeta = { ml_model: { label: 'ML Models', icon: Brain }, database: { label: 'Databases', icon: Database }, streaming: { label: 'Streaming', icon: Radio }, storage: { label: 'Storage', icon: HardDrive }, ai_search: { label: 'AI & Search', icon: Search } };
const svcIcons = { qdrant: Search, elasticsearch: Database, neo4j: GitBranch, redis: Gauge, kafka: Radio, minio: Box, bge_m3: Brain, bge_reranker: Workflow, minilm: Cpu, spacy: FileText, vane_search: Search, searxng: Database, lightpanda_browser: Cpu, pdf_parser: FileText };
const catColors = { ml_model: 'from-violet-500 to-purple-600', database: 'from-blue-500 to-indigo-600', streaming: 'from-orange-500 to-red-600', storage: 'from-emerald-500 to-teal-600', ai_search: 'from-pink-500 to-rose-600' };

export default function FeaturesIndex() {
  const [content, setContent] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    landingApi.get().then(r => setContent(r.data)).catch(() => {});
    landingApi.pricing().then(r => setPricingData(r.data)).catch(() => {});
  }, []);

  if (!content || !pricingData) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 brand-spinner rounded-full animate-spin" /></div>;

  const services = pricingData?.services || {};
  const fi = content?.features_index || {};
  const footer = content?.footer || {};
  const filtered = filter === 'all' ? Object.entries(services) : Object.entries(services).filter(([, s]) => s.category === filter);

  return (
    <div className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />
      <AnalyticsTracker />
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 left-1/3 w-[500px] h-[500px] brand-mesh-1 rounded-full blur-[120px] opacity-50" /></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{fi.title || 'Our Services'}</h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">{fi.subtitle || 'GPU-accelerated AI infrastructure, explained.'}</p>
        </div>
      </section>

      <section className="pb-20 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            <button onClick={() => setFilter('all')} className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${filter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600'}`}>All</button>
            {Object.entries(catMeta).map(([k, m]) => (
              <button key={k} onClick={() => setFilter(k)} className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${filter === k ? 'brand-btn-solid border-transparent' : 'bg-white border-slate-200 text-slate-600'}`}>
                <m.icon className="w-3 h-3" />{m.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(([id, svc]) => {
              const Icon = svcIcons[id] || Cpu;
              const gradient = catColors[svc.category] || 'brand-gradient-bg';
              return (
                <Link key={id} to={`/features/${id}`} data-testid={`feature-link-${id}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 hover:brand-border-soft hover:shadow-lg hover:brand-shadow transition-all duration-300">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-sm`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{svc.name}</h3>
                  <p className="text-sm text-slate-500 mb-3 leading-relaxed">{svc.friendly_description || svc.description}</p>
                  {(svc.use_cases || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {svc.use_cases.slice(0, 3).map(uc => <span key={uc} className="px-2 py-0.5 rounded-full bg-slate-50 text-[10px] font-medium text-slate-500 border border-slate-100">{uc}</span>)}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-slate-400">from ${Math.min(...Object.values(svc.pricing || {}).map(p => p.hourly_rate))}/hr</span>
                    <span className="text-xs font-semibold brand-text flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">Learn more <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <PublicFooter footer={footer} />
    </div>
  );
}
